import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../contexts/PermissionContext'
import { useAlerts } from '../../contexts/AlertContext'
import { messageAPI } from '../../services/api'
import BlockedAction, { BlockedBanner } from '../common/BlockedAction'
import { useActionGate } from '../../contexts/BlockPolicyContext'
import {
  HiOutlineXMark,
  HiOutlinePaperAirplane,
  HiOutlineMicrophone,
  HiOutlineDocument,
  HiOutlinePlay,
  HiOutlinePause,
  HiOutlineCheckCircle,
  HiOutlineFunnel,
  HiOutlineChatBubbleLeft,
  HiOutlineEye,
  HiOutlineUsers
} from 'react-icons/hi2'
import { getSecurityLevelConfig, getSecurityLevelOptionsForRole } from '../../config/messageSecurity'
import { formatDistanceToNow } from 'date-fns'

const MessagePanel = ({ 
  isOpen, 
  onClose, 
  entityType, 
  entityId,
  entityName 
}) => {
  const { user } = useAuth()
  const { userRole } = usePermissions()
  const { showSuccess, showError } = useAlerts()
  const messagesEndRef = useRef(null)

  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [audioPlayer, setAudioPlayer] = useState(null)
  const [playingMessageId, setPlayingMessageId] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [expandedReaders, setExpandedReaders] = useState(new Set()) // Track which messages have expanded reader lists
  
  const messagesContainerRef = useRef(null)
  const previousScrollHeight = useRef(0)
  
  // Create message form
  const [messageForm, setMessageForm] = useState({
    type: 'text',
    content: '',
    security_level: 'green',
    voice_file: null
  })
  const [sending, setSending] = useState(false)

  // Block-policy gates: text messages with files vs. voice messages have
  // separate policy keys. Voice playback gating is applied to the play
  // buttons below; here we only care about the send action.
  const sendAction = messageForm.type === 'voice' ? 'upload_voice' : 'upload_text'
  const sendGate = useActionGate(sendAction)
  const streamGate = useActionGate('stream_voice')

  // Filters
  const [filters, setFilters] = useState({
    type: '',
    security_level: '',
    is_read: ''
  })

  const securityLevelOptions = getSecurityLevelOptionsForRole(userRole)

  useEffect(() => {
    if (isOpen && entityId) {
      // Reset pagination and fetch initial messages
      setPage(1)
      setHasMore(true)
      setMessages([])
      fetchMessages(1, true)
    }
  }, [isOpen, entityId, filters])

  useEffect(() => {
    // Only auto-scroll to bottom on initial load
    if (messages.length > 0 && page === 1) {
      scrollToBottom()
    }
  }, [messages.length])

  useEffect(() => {
    // Add scroll listener for infinite scroll
    const container = messagesContainerRef.current
    if (!container) return

    const handleScroll = () => {
      // Check if scrolled to top (with 50px threshold)
      if (container.scrollTop < 50 && !loadingMore && hasMore) {
        loadMoreMessages()
      }
    }

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [loadingMore, hasMore, page])

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  const fetchMessages = async (pageNum = 1, isInitial = false) => {
    if (isInitial) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }

    try {
      const params = {
        page: pageNum,
        page_size: 10, // Load 10 messages at a time
        ordering: '-created_at', // Newest first from API
      }
      
      // Filter by entity type
      if (entityType === 'client') params.client = entityId
      if (entityType === 'case') params.case = entityId
      if (entityType === 'session') params.session = entityId

      // Add other filters
      if (filters.type) params.type = filters.type
      if (filters.security_level) params.security_level = filters.security_level
      if (filters.is_read) params.is_read = filters.is_read

      const response = await messageAPI.getMessages(params)
      
      if (response.success) {
        const data = response.data.data || response.data
        const messagesList = data.data || data.results || data
        
        // Check if there are more messages
        const hasNextPage = data.next !== null || (data.pagination && data.pagination.next)
        setHasMore(hasNextPage)

        if (isInitial) {
          // Initial load: reverse to show oldest first, newest at bottom
          setMessages(messagesList.reverse())
        } else {
          // Loading more: prepend older messages at the top
          // Save current scroll position
          if (messagesContainerRef.current) {
            previousScrollHeight.current = messagesContainerRef.current.scrollHeight
          }
          
          // Prepend older messages (reverse them so oldest is at top)
          setMessages(prev => [...messagesList.reverse(), ...prev])
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const loadMoreMessages = async () => {
    if (loadingMore || !hasMore) return
    
    const nextPage = page + 1
    setPage(nextPage)
    await fetchMessages(nextPage, false)
    
    // Restore scroll position after loading
    setTimeout(() => {
      if (messagesContainerRef.current) {
        const newScrollHeight = messagesContainerRef.current.scrollHeight
        const scrollDiff = newScrollHeight - previousScrollHeight.current
        messagesContainerRef.current.scrollTop = scrollDiff
      }
    }, 100)
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()

    // Early bail-out if the block policy blocks this action — server still
    // enforces, this just gives the user a clearer message.
    if (sendGate && sendGate.allowed === false) {
      showError(sendGate.message || 'This action is currently blocked.')
      return
    }

    if (messageForm.type === 'text' && !messageForm.content.trim()) {
      showError('Please enter message content')
      return
    }

    if (messageForm.type === 'voice' && !messageForm.voice_file) {
      showError('Please select a voice file')
      return
    }

    setSending(true)

    try {
      const submitData = new FormData()
      submitData.append('type', messageForm.type)
      submitData.append('security_level', messageForm.security_level)
      submitData.append('message_scope', 'direct')

      if (messageForm.type === 'text') {
        submitData.append('content', messageForm.content)
      } else if (messageForm.type === 'voice' && messageForm.voice_file) {
        submitData.append('voice_file', messageForm.voice_file)
      }

      // Add entity relationship
      if (entityType === 'client') submitData.append('client_id', entityId)
      if (entityType === 'case') submitData.append('case_id', entityId)
      if (entityType === 'session') submitData.append('session_id', entityId)

      const response = await messageAPI.createMessage(submitData)

      if (response.success) {
        showSuccess('Message sent!')
        setMessageForm({
          type: 'text',
          content: '',
          security_level: 'green',
          voice_file: null
        })
        
        // Add new message to the end and scroll to bottom
        const newMessage = response.data.data || response.data
        setMessages(prev => [...prev, newMessage])
        scrollToBottom()
      } else {
        showError(response.error?.message || 'Failed to send message')
      }
    } catch (error) {
      console.error('Error sending message:', error)
      showError('Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const handleMarkAsRead = async (messageId) => {
    try {
      const response = await messageAPI.markAsRead(messageId)
      
      if (response.success) {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === messageId
              ? { ...msg, is_read: true, read_count: (msg.read_count || 0) + 1 }
              : msg
          )
        )
      }
    } catch (error) {
      console.error('Error marking message as read:', error)
    }
  }

  const toggleReaderList = (messageId) => {
    setExpandedReaders(prev => {
      const newSet = new Set(prev)
      if (newSet.has(messageId)) {
        newSet.delete(messageId)
      } else {
        newSet.add(messageId)
      }
      return newSet
    })
  }

  const handlePlayVoice = (message) => {
    // If clicking on currently playing message, toggle pause/play
    if (playingMessageId === message.id && audioPlayer) {
      if (isPlaying) {
        audioPlayer.pause()
        setIsPlaying(false)
      } else {
        audioPlayer.play()
        setIsPlaying(true)
      }
      return
    }

    // Stop previous audio if playing
    if (audioPlayer) {
      audioPlayer.pause()
      audioPlayer.currentTime = 0
    }

    // Play new audio
    const audio = new Audio(message.voice_stream_url)
    setAudioPlayer(audio)
    setPlayingMessageId(message.id)
    setIsPlaying(true)

    audio.play().catch(err => {
      console.error('Error playing audio:', err)
      showError('Failed to play voice message')
      setAudioPlayer(null)
      setPlayingMessageId(null)
      setIsPlaying(false)
    })

    audio.onended = () => {
      setAudioPlayer(null)
      setPlayingMessageId(null)
      setIsPlaying(false)
    }

    audio.onpause = () => {
      setIsPlaying(false)
    }

    audio.onplay = () => {
      setIsPlaying(true)
    }
  }

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true })
    } catch {
      return dateString
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed right-0 top-16 bottom-0 w-full sm:w-[400px] md:w-[420px] lg:w-[450px] xl:w-[480px] bg-white border-l border-gray-200 shadow-xl z-40 flex flex-col slide-in-from-right-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand to-brand-hover text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <HiOutlineChatBubbleLeft className="w-6 h-6" />
            <div>
              <h2 className="font-semibold text-lg">Messages</h2>
              <p className="text-white/80 text-sm">{entityName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 hover:bg-brand-hover rounded-lg transition-colors"
            >
              <HiOutlineFunnel className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-brand-hover rounded-lg transition-colors"
            >
              <HiOutlineXMark className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters (Collapsible) */}
        {showFilters && (
          <div className="bg-gray-50 p-3 border-b space-y-2">
            <select
              value={filters.type}
              onChange={(e) => setFilters({...filters, type: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand"
            >
              <option value="">All Types</option>
              <option value="text">📝 Text</option>
              <option value="voice">🎤 Voice</option>
            </select>
            <select
              value={filters.security_level}
              onChange={(e) => setFilters({...filters, security_level: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand"
            >
              <option value="">All Security Levels</option>
              {securityLevelOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.icon} {option.label.split(' - ')[0]}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Messages List */}
        <div 
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
        >
          {/* Loading More Indicator (at top) */}
          {loadingMore && (
            <div className="flex items-center justify-center py-3">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-gray-600">Loading older messages...</span>
              </div>
            </div>
          )}
          
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Loading messages...</p>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                  <HiOutlineChatBubbleLeft className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">No messages yet</h3>
                <p className="text-gray-500 text-sm">Start the conversation below</p>
              </div>
            </div>
          ) : (
            messages.map((message) => {
              const securityConfig = getSecurityLevelConfig(message.security_level)
              const isOwn = user && message.created_by === user.id
              const showReaders = expandedReaders.has(message.id)
              const isBroadcast = message.message_scope === 'broadcast'

              return (
                <div
                  key={message.id}
                  className={`flex ${isBroadcast ? 'justify-center' : isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`${isBroadcast ? 'max-w-[95%]' : 'max-w-[85%]'} ${
                    isBroadcast 
                      ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-400' 
                      : isOwn 
                        ? 'bg-white border-2 border-cyan-500' 
                        : 'bg-white border border-gray-200'
                  } rounded-2xl shadow-sm p-3`}>
                    {/* Broadcast Badge or Sender & Time */}
                    {isBroadcast && (
                      <div className="mb-2 flex items-center justify-center gap-2">
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-orange-500 to-yellow-500 text-white">
                          📢 BROADCAST
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium ${isBroadcast ? 'text-orange-700' : isOwn ? 'text-cyan-600' : 'text-gray-900'}`}>
                          {isOwn ? 'You' : message.created_by_name}
                        </span>
                        {!isBroadcast && (
                          <span className={`px-2 py-0.5 rounded-full text-xs ${securityConfig.bgColor} ${securityConfig.textColor}`}>
                            {securityConfig.icon}
                          </span>
                        )}
                      </div>
                      {!isOwn && !message.is_read && (
                        <button
                          onClick={() => handleMarkAsRead(message.id)}
                          className="text-xs opacity-70 hover:opacity-100"
                          title="Mark as read"
                        >
                          <HiOutlineCheckCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Title */}
                    {message.title && (
                      <p className="text-sm font-semibold text-gray-900 mb-1">{message.title}</p>
                    )}

                    {/* Content */}
                    {message.type === 'text' && message.content && (
                      <p className={`text-sm mb-2 whitespace-pre-wrap break-words overflow-wrap-anywhere ${isOwn ? 'text-gray-700 bg-cyan-50 rounded-lg p-2' : 'text-gray-700 bg-gray-50 rounded-lg p-2'}`}>
                        {message.content}
                      </p>
                    )}

                    {/* Voice Message — playback gated by stream_voice policy */}
                    {message.type === 'voice' && message.voice_stream_url && (
                      <div className={`flex items-center gap-2 mb-2 ${isOwn ? 'bg-cyan-50' : 'bg-gray-50'} rounded-lg p-2`}>
                        <BlockedAction action="stream_voice" badgeSize="sm">
                          <button
                            onClick={() => handlePlayVoice(message)}
                            className={`p-2 rounded-full ${isOwn ? 'bg-cyan-500 hover:bg-cyan-600' : 'bg-cyan-500 hover:bg-cyan-600'} transition-all`}
                            title={playingMessageId === message.id && isPlaying ? 'Pause' : 'Play'}
                          >
                            {playingMessageId === message.id && isPlaying ? (
                              <HiOutlinePause className="w-4 h-4 text-white" />
                            ) : (
                              <HiOutlinePlay className="w-4 h-4 text-white" />
                            )}
                          </button>
                        </BlockedAction>
                        <span className={`text-xs ${isOwn ? 'text-cyan-700' : 'text-gray-600'}`}>
                          {playingMessageId === message.id && isPlaying ? 'Playing...' : 'Voice message'}
                        </span>
                      </div>
                    )}

                    {/* Time & Status */}
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-xs ${isOwn ? 'text-gray-500' : 'text-gray-500'}`}>
                        {formatDate(message.created_at)}
                      </span>
                      {isOwn ? (
                        /* Sender View - Show read count with expandable list */
                        <div className="flex flex-col items-end">
                          <button
                            onClick={() => toggleReaderList(message.id)}
                            className={`text-xs ${isOwn ? 'text-cyan-600 hover:text-cyan-700' : 'text-gray-500'} flex items-center gap-1`}
                          >
                            <HiOutlineEye className="w-3 h-3" />
                            {message.read_count}
                          </button>
                        </div>
                      ) : (
                        /* Recipient View - Show read status */
                        message.is_read && (
                          <span className={`text-xs ${isOwn ? 'text-cyan-600' : 'text-gray-500'}`}>
                            ✓ Read
                          </span>
                        )
                      )}
                    </div>

                    {/* Readers List (for sender only) */}
                    {isOwn && showReaders && message.read_by_users && message.read_by_users.length > 0 && (
                      <div className="mt-2 bg-cyan-50 border border-cyan-200 rounded-lg p-2">
                        <p className="text-xs font-semibold text-cyan-700 mb-1">Read by:</p>
                        <div className="space-y-1">
                          {message.read_by_users.map((reader) => (
                            <div key={reader.id} className="flex items-center justify-between text-xs">
                              <span className="text-gray-700">{reader.name}</span>
                              {reader.read_at && (
                                <span className="text-gray-600 text-[10px]">
                                  {formatDistanceToNow(new Date(reader.read_at), { addSuffix: true })}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input Area */}
        <div className="bg-white border-t p-4">
          {/* Block-policy banner — only shows when current action is gated */}
          <BlockedBanner action={sendAction} className="mb-3" />
          <form onSubmit={handleSendMessage} className="space-y-3">
            {/* Type Selector */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMessageForm({...messageForm, type: 'text', voice_file: null})}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  messageForm.type === 'text'
                    ? 'bg-brand text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <HiOutlineDocument className="w-4 h-4 inline mr-1" />
                Text
              </button>
              <button
                type="button"
                onClick={() => setMessageForm({...messageForm, type: 'voice', content: ''})}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  messageForm.type === 'voice'
                    ? 'bg-brand text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <HiOutlineMicrophone className="w-4 h-4 inline mr-1" />
                Voice
              </button>
            </div>

            {/* Security Level */}
            <select
              value={messageForm.security_level}
              onChange={(e) => setMessageForm({...messageForm, security_level: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand"
            >
              {securityLevelOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.icon} {option.label.split(' - ')[0]}
                </option>
              ))}
            </select>

            {/* Text Input */}
            {messageForm.type === 'text' ? (
              <textarea
                value={messageForm.content}
                onChange={(e) => setMessageForm({...messageForm, content: e.target.value})}
                placeholder="Type your message..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand resize-none"
              />
            ) : (
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => setMessageForm({...messageForm, voice_file: e.target.files[0]})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand text-sm"
              />
            )}

            {/* Send Button — gated by block policy when subscription/Drive
                disallows uploads in this workspace */}
            <BlockedAction action={sendAction} mode="inline" className="block w-full">
              <button
                type="submit"
                disabled={sending}
                className="w-full py-3 bg-gradient-to-r from-brand to-brand-hover text-white rounded-lg hover:from-brand-hover hover:to-brand-hover transition-all flex items-center justify-center gap-2 font-medium disabled:opacity-50"
              >
                {sending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <HiOutlinePaperAirplane className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>
            </BlockedAction>
          </form>
        </div>
    </div>
  )
}

export default MessagePanel

